import { useCopilotChatInternal } from '@copilotkit/react-core';
import { MessagesProps, useChatContext } from '@copilotkit/react-ui';
import { Message } from '@copilotkit/shared';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import localeService from 'src/dataServices/LocaleService';
import doTextSubstitution from 'src/common/doTextSubstitution';
import { SkeletonLoader } from './SkeletonLoader';
import {
  useArrowNavigationGroup,
} from "@fluentui/react-components";

function makeInitialMessages(initial: string | string[] | undefined): Message[] {
  if (!initial) return [];
  if (Array.isArray(initial)) {
    return initial.map((message) => ({ id: message, role: 'assistant' as const, content: message }));
  }
  return [{ id: initial, role: 'assistant' as const, content: initial }];
}

function useScrollToBottom(messages: Message[]) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const isProgrammaticScrollRef = useRef(false);
  const isUserScrollUpRef = useRef(false);

  const scrollToBottom = () => {
    if (messagesContainerRef.current && messagesEndRef.current) {
      isProgrammaticScrollRef.current = true;
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  const handleScroll = () => {
    if (isProgrammaticScrollRef.current) {
      isProgrammaticScrollRef.current = false;
      return;
    }
    if (messagesContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
      isUserScrollUpRef.current = scrollTop + clientHeight < scrollHeight;
    }
  };

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) container.addEventListener('scroll', handleScroll);
    return () => {
      if (container) container.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const mutationObserver = new MutationObserver(() => {
      if (!isUserScrollUpRef.current) scrollToBottom();
    });
    mutationObserver.observe(container, { childList: true, subtree: true, characterData: true });
    return () => mutationObserver.disconnect();
  }, []);

  const userMessageCount = useMemo(() => messages.filter((m) => m.role === 'user').length, [messages]);

  useEffect(() => {
    isUserScrollUpRef.current = false;
    scrollToBottom();
  }, [userMessageCount]);

  return { messagesEndRef, messagesContainerRef };
}

export const CustomMessages: React.FC<MessagesProps> = ({
  inProgress,
  children,
  RenderMessage,
  AssistantMessage,
  UserMessage,
  ErrorMessage,
  ImageRenderer,
  onRegenerate,
  onCopy,
  onThumbsUp,
  onThumbsDown,
  messageFeedback,
  markdownTagRenderers,
  chatError
}) => {
  const { labels } = useChatContext();
  const { messages: visibleMessages, interrupt } = useCopilotChatInternal();
  const initialMessages = useMemo(() => makeInitialMessages(labels.initial), [labels.initial]);
  const messages = useMemo(() => [...initialMessages, ...visibleMessages], [initialMessages, visibleMessages]);
  const { messagesContainerRef, messagesEndRef } = useScrollToBottom(messages);




  // Show our shimmer when no assistant content has arrived after the last user message.
  // Suppress it when CopilotKit's built-in activityIcon is already visible
  // (which shows when the very last message is from the user AND inProgress is true).


  // Hide the error banner when the user sends a new message; re-show only when a new error arrives.
  const prevUserMsgCountRef = useRef(0);
  const [showChatError, setShowChatError] = useState(false);

  useEffect(() => {
    setShowChatError(!!chatError);
  }, [chatError]);

  useEffect(() => {
    const userMsgCount = messages.filter((m) => m.role === 'user').length;
    if (userMsgCount !== prevUserMsgCountRef.current) {
      setShowChatError(false);
      prevUserMsgCountRef.current = userMsgCount;
    }
  }, [messages]);

  // Show shimmer until an assistant message with actual content exists after
  // the latest user message, regardless of the inProgress flag.
  const lastMessage = messages[messages.length - 1] as (Message & { name?: string }) | undefined;
  const showLoading = lastMessage?.name === 'coagent-state-render';

  const properties = useArrowNavigationGroup({axis: 'vertical', memorizeCurrent: false, tabbable: true});

  // Announce completed assistant messages to screen readers only once,
  // after streaming finishes (inProgress transitions from true to false).
  const [liveAnnouncement, setLiveAnnouncement] = useState('');
  const wasInProgressRef = useRef(false);
  // Track which assistant message id we last announced, so an inProgress
  // flicker between turns (e.g. when a suggested chip is clicked) doesn't
  // re-announce the previous turn's content.
  const lastAnnouncedIdRef = useRef<string | null>(null);
  // Pending announcement timer. The model often streams text first, briefly
  // flips inProgress=false, and then attaches toolCalls (which renders a form
  // / card we don't want to read aloud as preface text). Debounce by waiting a
  // short window before announcing; if inProgress flips back to true or
  // toolCalls appear, we cancel.
  const announceTimerRef = useRef<number | null>(null);

  const getLastAssistantMessage = useCallback(():
    | (Message & { toolCalls?: unknown[] })
    | undefined => {
    const last = messages[messages.length - 1] as
      | (Message & { toolCalls?: unknown[] })
      | undefined;
    if (!last || last.role !== 'assistant') return undefined;
    return last;
  }, [messages]);

  const getAssistantTextContent = useCallback(
    (msg: (Message & { toolCalls?: unknown[] }) | undefined): string => {
      // If the latest assistant turn includes a tool-call render (e.g. the
      // support ticket form), suppress the text announcement -- the rendered
      // form/card handles its own announcement and we'd otherwise read the
      // assistant's preface text alongside the new UI.
      if (!msg) return '';
      if (Array.isArray(msg.toolCalls) && msg.toolCalls.length > 0) return '';
      return typeof msg.content === 'string' ? msg.content : '';
    },
    []
  );

  useEffect(() => {
    const cancelPending = () => {
      if (announceTimerRef.current !== null) {
        window.clearTimeout(announceTimerRef.current);
        announceTimerRef.current = null;
      }
    };

    if (inProgress) {
      wasInProgressRef.current = true;
      cancelPending();
      return;
    }
    if (wasInProgressRef.current) {
      wasInProgressRef.current = false;
      cancelPending();
      // Wait briefly for any trailing toolCalls/inProgress flip before
      // committing the announcement. Re-evaluate the latest assistant message
      // at fire time so a tool-call render that lands during the wait
      // suppresses the text announcement.
      announceTimerRef.current = window.setTimeout(() => {
        announceTimerRef.current = null;
        const last = getLastAssistantMessage();
        const completedText = getAssistantTextContent(last);
        if (completedText && last?.id && last.id !== lastAnnouncedIdRef.current) {
          lastAnnouncedIdRef.current = last.id;
          // Clear first, then set -- ensures re-announcement even if content is similar
          setLiveAnnouncement('');
          requestAnimationFrame(() => setLiveAnnouncement(completedText));
        }
      }, 500);
      return;
    }
    // Idle: keep the last-announced id in sync with the most recent assistant
    // message in history. This way, if the user reopens the chat or a turn
    // begins with a brief inProgress flicker, we do not re-announce historical
    // content that the user has already seen/heard.
    const last = getLastAssistantMessage();
    if (last?.id) {
      lastAnnouncedIdRef.current = last.id;
    }
  }, [inProgress, getLastAssistantMessage, getAssistantTextContent]);

  useEffect(
    () => () => {
      if (announceTimerRef.current !== null) {
        window.clearTimeout(announceTimerRef.current);
      }
    },
    []
  );

  return (
    <div className="copilotKitMessages" ref={messagesContainerRef}>
      <div 
        className="copilotKitMessagesContainer"
        role="log"
        aria-label={localeService.getLocaleContent().chatMessagesAriaLabel}
        aria-live="off"
        {...properties}
      >
        {messages.map((message, index) => {
          const isCurrentMessage = index === messages.length - 1;
          
          return (
            <div
              key={`container-${message.id}`}
              tabIndex={0}
              role="article"
              aria-label={doTextSubstitution(localeService.getLocaleContent().chatMessageRoleAriaLabel || '{role}', { role: message.role })}
              className="copilot-message-wrapper"
            >
              <RenderMessage
                message={message}
                messages={messages}
                inProgress={inProgress}
                index={index}
                isCurrentMessage={isCurrentMessage}
                AssistantMessage={AssistantMessage}
                UserMessage={UserMessage}
                ImageRenderer={ImageRenderer}
                onRegenerate={onRegenerate}
                onCopy={onCopy}
                onThumbsUp={onThumbsUp}
                onThumbsDown={onThumbsDown}
                messageFeedback={messageFeedback}
                markdownTagRenderers={markdownTagRenderers}
              />
            </div>
          );
        })}
        {showLoading && <SkeletonLoader />}
        {interrupt}
        {chatError && showChatError && ErrorMessage && <ErrorMessage error={chatError} isCurrentMessage />}
      </div>
      <footer className="copilotKitMessagesFooter" ref={messagesEndRef}>
        {children}
      </footer>
      <div
        className="copilot-sr-only"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {liveAnnouncement}
      </div>
    </div>
  );
};
